import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  useMemo,
  useContext,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import "./mic-styling.css";
import { Mic, Loader2, Waves, AlertCircle, Globe, Square } from "lucide-react";
import { useMicVAD } from "@ricky0123/vad-react";
import { getDefaultRealTimeVADOptions } from "@ricky0123/vad-web";
import { io } from "socket.io-client";
import { motion, AnimatePresence } from "framer-motion";
import { useAppContext } from "../../../context/AppContext";
import useLocalization from "../../../hooks/useLocalization";
import micButtonBg from "../../../assets/images/circular_mic_container.png";
import { DialogContext } from "../Dialog/DialogContext";
import PropTypes from "prop-types";

import {
  detectBrowser,
  applyBrowserFixes,
  getOptimizedSettings,
  getRequiredWorkarounds,
  validateBrowserCompatibility,
} from "./browserUtils";

const BROWSER_TYPE = detectBrowser();
const BROWSER_SETTINGS = getOptimizedSettings();
const BROWSER_WORKAROUNDS = getRequiredWorkarounds();

const SAMPLE_RATE = 16000;
const SILENCE_TIMEOUT = BROWSER_SETTINGS.timing.silenceTimeout;
const POST_SILENCE_GRACE_PERIOD =
  BROWSER_SETTINGS.timing.postSilenceGracePeriod;
const MAX_BUFFER_SIZE = BROWSER_SETTINGS.buffers.maxBufferSize;
const ALD_RETRY_ATTEMPTS = 2;
import micIcon from "../../../assets/images/mic_icon.svg";
import voiceAssistantGif from "../../../assets/gifs/voice_assis.gif";
import { useLogger } from "../../../utils/UnifiedLogger";
import {
  selectIsTextLanguageDetecting,
  selectIsContentGenerating,
  selectIsNerDetecting,
  selectDisplayTranscript,
  selectTranscriptType,
  selectAccumulatedFinalTranscript,
  selectCurrentInterimTranscript,
  selectSelectedSocketLanguageData,
  selectIsSocketRecording,
  selectSelectedChatId,
  selectIsNotRelatedToCenter,
} from "../../../store/features/message/textContentSelectors";

import {
  setSocketFlags,
  setDisplayTranscript,
  setIsSocketRecording,
  setSelectedSocketLanguageData,
  setTranscriptType,
  setAccumulatedFinalTranscript,
  setCurrentInterimTranscript,
  setRecordedBase64Data,
  setIsContentGenerating,
  setIsTextLanguageDetecting,
} from "../../../store/features/message/textContentSlice";

import { getLanguageData } from "../../../utils/AppConstants";
import { updateFlowStates } from "../../../store/features/message/updateFlowStates";
import {
  setIsBigMicButtonClicked,
  setIsMicButtonClicked,
  setIsNewChatClicked,
  setIsSendTriggeredFromSocket,
  setIsSuggestionsVisible,
  setIsTextFromBigMicButton,
} from "../../../store/features/ui/uiSlice";
import TooltipBubble from "../Tooltip/TooltipBubble";
import {
  selectIsBigMicButtonClicked,
  selectIsNewChatClicked,
  selectMicButtonDisabled,
} from "../../../store/features/ui/uiSelectors";
import { getChatConfig } from "../../../utils/chatConfigStorage";
import { useLanguageDetection } from "../../../hooks/useLanguageDetection";
import { API_ENDPOINTS } from "../../../utils/constants";
import { KpmgTransliterate } from "../TranslitrationSuggestion/KpmgTransliterate";
import { TextareaAutosize } from "@mui/material";
import { resetAppChatState } from "../../../utils/appReset/resetAppChatState";

const bars = [
  { height: "h-1", delay: 0 },
  { height: "h-2", delay: 0.2 },
  { height: "h-1", delay: 0.4 },
  { height: "h-2", delay: 0.5 },
  { height: "h-1", delay: 0.6 },
];

const SocketMicStreamer = forwardRef(
  (
    { isUserInputBar = false, onAutoSend = () => {}, normalButton = false },
    ref
  ) => {
    const { logInfo } = useLogger("SocketMicStreamer", true);
    const dispatch = useDispatch();
    const { sharedApiService, setSelectedLanguageData, setIsNewChat } =
      useAppContext();
    const aldRequestIdRef = useRef(null);
    const previousTranscriptRef = useRef("");
    const aldFailedHardRef = useRef(false);
    const hasReceivedTranscriptRef = useRef(false);
    // const startedFromBigMicRef = useRef(false);
    const { detectLanguage: detectLanguageFromHook } = useLanguageDetection();
    const detectLanguageFromHookRef = useRef(detectLanguageFromHook);
    const { showError, showErrorAsync, showConfirm } =
      useContext(DialogContext);
    const [editableTranscript, setEditableTranscript] = useState("");
    const isNewChatClicked = useSelector(selectIsNewChatClicked);

    const { t, setLanguage } = useLocalization();

    const displayTranscript = useSelector(selectDisplayTranscript);
    const transcriptType = useSelector(selectTranscriptType);
    const accumulatedFinalTranscript = useSelector(
      selectAccumulatedFinalTranscript
    );
    const currentInterimTranscript = useSelector(
      selectCurrentInterimTranscript
    );

    const combinedTranscript = [
      accumulatedFinalTranscript,
      currentInterimTranscript,
    ]
      .filter(Boolean)
      .join(" ");

    const selectedSocketLanguageData = useSelector(
      selectSelectedSocketLanguageData
    );
    const getIsSocketRecording = useSelector(selectIsSocketRecording);
    const isLanguageDetecting = useSelector(selectIsTextLanguageDetecting);
    const isContentGenerating = useSelector(selectIsContentGenerating);
    const isNerDetecting = useSelector(selectIsNerDetecting);
    const isMicButtonDisabled = useSelector(selectMicButtonDisabled);
    const selectedChatId = useSelector(selectSelectedChatId);
    const isNotRelatedToCenter = useSelector(selectIsNotRelatedToCenter);

    const accumulatedTranscriptRef = useRef("");
    const aldAudioBufferRef = useRef([]);
    const aldInProgressRef = useRef(false);
    const aldRetryCountRef = useRef(0);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const fallbackTriggeredRef = useRef(false);
    const audioQualityBufferRef = useRef([]);
    const completeAudioBufferRef = useRef([]);
    const continuousSpeechDurationRef = useRef(0);
    const gracePeriodTimeoutRef = useRef(null);
    const hasDetectedLanguageRef = useRef(false);
    const aldCompletedRef = useRef(false);
    const isFinalRecoveryRef = useRef(false);
    const isProcessingFinalAudioRef = useRef(false);
    const isSilenceProcessingRef = useRef(false);
    const isSocketInitializingRef = useRef(false);
    const isSocketReadyRef = useRef(false);
    const isUserSpeaking = useRef(false);
    const lastALDAttemptRef = useRef(0);
    const lastFinalTranscriptRef = useRef("");
    const mediaStreamRef = useRef(null);
    const postAldAudioBufferRef = useRef([]);
    const processorNodeRef = useRef(null);
    const sendIntervalRef = useRef(null);
    const silenceTimeoutRef = useRef(null);
    const socketInitPromiseRef = useRef(null);
    const socketRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const speechStartTimeRef = useRef(null);
    const totalAudioDurationRef = useRef(0);
    const transcriptRef = useRef(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isFallbackInProgress, setIsFallbackInProgress] = useState(false);
    const [isSocketErr, setIsSocketError] = useState(false);
    // ADD THIS: Detect mobile device
    const [isMobile, setIsMobile] = useState(false);

    // useEffect(() => {
    //   const shouldSync = !getIsSocketRecording || !editableTranscript;

    //   shouldSync && setEditableTranscript(combinedTranscript);
    // }, [combinedTranscript]);

    const [flags, setFlags] = useState({
      aldProcessingTime: 0,
      aldRetryCount: 0,
      audioLength: 0,
      audioQuality: "good",
      browserCompatible: true,
      browserType: BROWSER_TYPE,
      bufferStatus: "empty",
      detectedLanguage: "",
      detectingLanguage: false,
      error: null,
      initializing: false,
      languageDetected: false,
      listening: false,
      processing: false,
      socketConnecting: false,
      socketReady: false,
      streaming: false,
    });

    const apiKey = import.meta.env.VITE_BHASHINI_API_KEY;
    const ALD_SERVICE_ID = import.meta.env.VITE_BHASHINI_ALD_SERVICE_ID;
    const LANG_DETECTION_ENDPOINT_URL = import.meta.env
      .VITE_BHASHINI_ENDPOINT_URL;
    const serverUrl =
      import.meta.env.VITE_BHASHINI_ASR_WS_URL ||
      "wss://dhruva-api.bhashini.gov.in";
    const socketType = "asrasa";

    useEffect(() => {
      if (editableTranscript?.trim()) {
        previousTranscriptRef.current = editableTranscript;
      }
    }, [editableTranscript]);

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768); // 768px is md breakpoint
      };

      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const handleNewChat = () => {
      resetAppChatState({
        dispatch,
        sharedApiService,
        setSelectedLanguageData,
      });

      setIsNewChat(true);
      dispatch(setIsNewChatClicked(true));
    };

    useEffect(() => {
      if (!isNewChatClicked) return;

      const hardReset = async () => {
        logInfo("NEW_CHAT_RESET", "Resetting mic streamer to initial state");

        // 1️⃣ Stop everything safely
        await stopStreaming();

        // 2️⃣ Reset local UI state
        setEditableTranscript("");
        setIsLoading(false);
        setIsFallbackInProgress(false);
        setIsSocketError(false);

        // 3️⃣ Reset internal refs
        accumulatedTranscriptRef.current = "";
        lastFinalTranscriptRef.current = "";
        hasReceivedTranscriptRef.current = false;
        hasDetectedLanguageRef.current = false;
        aldCompletedRef.current = false;
        aldInProgressRef.current = false;
        aldRetryCountRef.current = 0;
        lastALDAttemptRef.current = 0;
        isFinalRecoveryRef.current = false;
        isProcessingFinalAudioRef.current = false;
        isSocketReadyRef.current = false;
        isSocketInitializingRef.current = false;
        fallbackTriggeredRef.current = false;

        audioChunksRef.current = [];
        aldAudioBufferRef.current = [];
        audioQualityBufferRef.current = [];
        postAldAudioBufferRef.current = [];
        completeAudioBufferRef.current = [];
        totalAudioDurationRef.current = 0;
        continuousSpeechDurationRef.current = 0;

        // 4️⃣ Reset flags UI state
        updateFlags({
          listening: false,
          streaming: false,
          detectingLanguage: false,
          languageDetected: false,
          detectedLanguage: "",
          bufferStatus: "idle",
          processing: false,
          socketConnecting: false,
          socketReady: false,
          initializing: false,
          error: null,
          audioLength: 0,
          aldProcessingTime: 0,
          aldRetryCount: 0,
        });

        logInfo("NEW_CHAT_RESET_DONE", "Mic streamer fully reset");
      };
      setSelectedSocketLanguageData({ code: "en", label: "English" });
      hardReset();
    }, [isNewChatClicked]);

    useEffect(() => {
      accumulatedTranscriptRef.current = accumulatedFinalTranscript;
    }, [accumulatedFinalTranscript]);

    useEffect(() => {
      if (transcriptRef.current) {
        transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
      }
    }, [accumulatedFinalTranscript, currentInterimTranscript]);

    useEffect(() => {
      const initializeBrowser = () => {
        try {
          applyBrowserFixes();
          const compatibility = validateBrowserCompatibility();
          updateFlags({
            browserType: BROWSER_TYPE,
            browserCompatible: compatibility.compatible,
          });

          if (!compatibility.compatible) {
            updateFlags({
              error: `Browser compatibility issues: ${compatibility.issues.join(
                ", "
              )}`,
            });
            logInfo(
              "BROWSER_ERROR",
              "Compatibility issues detected",
              compatibility
            );
          } else {
            logInfo("BROWSER_INIT", "Browser initialized successfully", {
              browser: BROWSER_TYPE,
              settings: BROWSER_SETTINGS,
              workarounds: BROWSER_WORKAROUNDS,
            });
          }
          if (compatibility.warnings.length > 0) {
            compatibility.warnings.forEach((warning) => {
              logInfo("BROWSER_WARNING", warning);
            });
          }
        } catch (error) {
          logInfo("BROWSER_INIT_ERROR", error.message);
          updateFlags({
            error: `Browser initialization failed: ${error.message}`,
            browserCompatible: false,
          });
        }
      };
      initializeBrowser();
    }, []);

    const updateFlags = (newFlags) => {
      setFlags((prev) => {
        const updated = { ...prev, ...newFlags };
        return updated;
      });
    };

    useEffect(() => {
      dispatch(setSocketFlags(flags));
    }, [flags, setSocketFlags]);

    const isLanguageExist = () => {
      if (selectedChatId) {
        const chatConfig = getChatConfig();
        if (
          chatConfig &&
          typeof chatConfig.language === "string" &&
          chatConfig.language.trim()
        ) {
          return true;
        }
      }

      return (
        selectedSocketLanguageData &&
        typeof selectedSocketLanguageData === "object" &&
        Object.keys(selectedSocketLanguageData).length > 0 &&
        selectedSocketLanguageData.code?.trim()
      );
    };

    //   const getLanguageCode = () => {
    //     return isLanguageExist() ? selectedSocketLanguageData.code : "en";
    //   };

    const callRestASRAsFallback = async (base64Audio) => {
      try {
        if (!base64Audio) return;

        logInfo(
          "VOICE_FALLBACK_PRE_SEND",
          "Calling REST ASR for final completion"
        );

        // ✅ LOCK UI
        setIsFallbackInProgress(true);

        await sharedApiService.sendRequest({
          method: "POST",
          url: API_ENDPOINTS.speech2text,
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            audio: base64Audio,
            source_language: getLanguageCode(),
          },
          abortable: true,
          isEncrypted: false,
          onStart: () => {
            // dispatch(setIsSocketRecording(false));
            dispatch(setIsContentGenerating(true));
            dispatch(setIsTextLanguageDetecting(true));
          },

          onSuccess: (res) => {
            const transcript =
              res?.output?.trim() || res?.eng_text?.trim() || "";

            if (!transcript) return;

            dispatch(setAccumulatedFinalTranscript(transcript));
            dispatch(setCurrentInterimTranscript(""));
            dispatch(setTranscriptType("final"));
            dispatch(updateFlowStates());

            logInfo(
              "VOICE_FALLBACK_SUCCESS",
              "ASR corrected final transcript",
              {
                transcript,
              }
            );
            setEditableTranscript(transcript);
            dispatch(setIsContentGenerating(false));
            dispatch(setIsTextLanguageDetecting(false));
          },
          onComplete: () => {
            // ✅ UNLOCK UI
            setIsFallbackInProgress(false);
            dispatch(setIsContentGenerating(false));
            dispatch(setIsTextLanguageDetecting(false));
          },

          onError: () => {
            setIsFallbackInProgress(false);
            dispatch(setIsContentGenerating(false));
            dispatch(setIsTextLanguageDetecting(false));
          },
        });
      } catch (err) {
        logInfo("VOICE_FALLBACK_FAILED", err.message);
        setIsFallbackInProgress(false);
      }
    };

    const getLanguageCode = () => {
      // 1️⃣ Read from chatConfig ONLY if chat exists
      if (selectedChatId) {
        const chatConfig = getChatConfig();
        if (
          chatConfig &&
          typeof chatConfig.language === "string" &&
          chatConfig.language.trim()
        ) {
          return chatConfig.language.trim();
        }
      }

      // 2️⃣ Default behavior (UNCHANGED)
      if (
        selectedSocketLanguageData &&
        typeof selectedSocketLanguageData.code === "string" &&
        selectedSocketLanguageData.code.trim()
      ) {
        return selectedSocketLanguageData.code.trim();
      }

      return null;
    };

    const assessAudioQuality = (audioChunks) => {
      if (!audioChunks || audioChunks.length === 0) {
        return {
          quality: "poor",
          reason: "No audio data",
          rms: 0,
          dynamicRange: 0,
        };
      }

      try {
        const totalLength = audioChunks.reduce(
          (acc, chunk) => acc + chunk.length,
          0
        );
        const combined = new Float32Array(totalLength);

        let offset = 0;
        for (const chunk of audioChunks) {
          if (chunk && chunk.length) {
            combined.set(chunk, offset);
            offset += chunk.length;
          }
        }

        if (combined.length === 0) {
          return {
            quality: "poor",
            reason: "Empty combined audio",
            rms: 0,
            dynamicRange: 0,
          };
        }

        const rms = Math.sqrt(
          combined.reduce((sum, sample) => sum + sample * sample, 0) /
            combined.length
        );

        let max = -Infinity;
        let min = Infinity;

        for (const value of combined) {
          if (value > max) max = value;
          if (value < min) min = value;
        }

        const dynamicRange = max - min;
        const duration = combined.length / SAMPLE_RATE;

        let quality = "good";
        let reason = "Audio quality acceptable";

        if (rms < 0.001) {
          quality = "poor";
          reason = "Audio too quiet (RMS < 0.001)";
        } else if (rms > 0.8) {
          quality = "poor";
          reason = "Audio too loud/clipped (RMS > 0.8)";
        } else if (dynamicRange < 0.01) {
          quality = "poor";
          reason = "Audio too flat (no dynamic range)";
        } else if (duration < 0.5) {
          quality = "poor";
          reason = `Audio too short (${duration.toFixed(1)}s < 0.5s)`;
        } else if (rms < 0.005) {
          quality = "fair";
          reason = "Audio quiet but acceptable";
        } else if (duration < 1.0) {
          quality = "fair";
          reason = "Audio short but acceptable";
        }

        return { quality, reason, rms, dynamicRange, duration };
      } catch (error) {
        logInfo("AUDIO_QUALITY_ERROR", error.message);
        return {
          quality: "poor",
          reason: "Quality assessment failed",
          rms: 0,
          dynamicRange: 0,
        };
      }
    };

    const convertAudioData = (audioData) => {
      if (!audioData || audioData.length === 0) {
        logInfo("CONVERT_AUDIO_WARNING", "Empty audio data provided");
        return [];
      }

      try {
        return Array.from(
          new Int16Array(
            audioData.map((s) => {
              const clampedSample = Math.max(-1, Math.min(1, s));
              return clampedSample < 0
                ? clampedSample * 0x8000
                : clampedSample * 0x7fff;
            })
          )
        );
      } catch (error) {
        logInfo("CONVERT_AUDIO_ERROR", error.message);
        return [];
      }
    };

    const getCombinedAudioBase64 = async (chunks) => {
      try {
        if (!chunks || chunks.length === 0) {
          throw new Error("No audio chunks provided");
        }

        const validChunks = chunks.filter(
          (chunk) => chunk && chunk.length > 0 && chunk instanceof Float32Array
        );

        if (validChunks.length === 0) {
          throw new Error("No valid audio chunks found");
        }

        const totalLength = validChunks.reduce(
          (acc, chunk) => acc + chunk.length,
          0
        );
        if (totalLength === 0) {
          throw new Error("Total audio length is zero");
        }

        const combined = new Float32Array(totalLength);
        let offset = 0;

        for (const chunk of validChunks) {
          combined.set(chunk, offset);
          offset += chunk.length;
        }

        const qualityAssessment = assessAudioQuality([combined]);
        updateFlags({ audioQuality: qualityAssessment.quality });

        const buffer = new ArrayBuffer(44 + combined.length * 2);
        const view = new DataView(buffer);

        const writeString = (offset, str) => {
          for (let i = 0; i < str.length; i++) {
            view.setUint8(offset + i, str.charCodeAt(i));
          }
        };

        writeString(0, "RIFF");
        view.setUint32(4, 36 + combined.length * 2, true);
        writeString(8, "WAVE");
        writeString(12, "fmt ");
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, 1, true);
        view.setUint32(24, SAMPLE_RATE, true);
        view.setUint32(28, SAMPLE_RATE * 2, true);
        view.setUint16(32, 2, true);
        view.setUint16(34, 16, true);
        writeString(36, "data");
        view.setUint32(40, combined.length * 2, true);

        for (let i = 0; i < combined.length; i++) {
          let sample = combined[i];
          if (Math.abs(sample) > 1.0) {
            sample = sample / Math.abs(sample);
          }
          const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
          view.setInt16(44 + i * 2, Math.round(intSample), true);
        }

        return new Promise((resolve, reject) => {
          try {
            const blob = new Blob([buffer], { type: "audio/wav" });
            if (blob.size === 0) {
              reject(new Error("Generated blob is empty"));
              return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
              try {
                const result = reader.result;
                if (!result || typeof result !== "string") {
                  reject(new Error("FileReader result is invalid"));
                  return;
                }

                const base64 = result.split(",")[1];
                if (!base64 || base64.length === 0) {
                  reject(new Error("Base64 conversion failed"));
                  return;
                }
                resolve(base64);
              } catch (error) {
                reject(
                  new Error(`FileReader processing failed: ${error.message}`)
                );
              }
            };

            reader.onerror = () => {
              reject(
                new Error(
                  `FileReader error: ${
                    reader.error?.message || "Unknown error"
                  }`
                )
              );
            };

            reader.readAsDataURL(blob);
          } catch (error) {
            reject(new Error(`Blob creation failed: ${error.message}`));
          }
        });
      } catch (error) {
        logInfo(
          "AUDIO_CONVERSION_ERROR",
          `Conversion failed: ${error.message}`
        );
        throw new Error(`Audio conversion failed: ${error.message}`);
      }
    };

    const detectLanguage = async (audioChunks, reason = "UNKNOWN") => {
      if (
        aldInProgressRef.current ||
        hasDetectedLanguageRef.current ||
        isLanguageExist()
      ) {
        logInfo(
          "ALD",
          "Skipping - already in progress, completed, or predefined language exists"
        );
        return getLanguageCode();
      }

      const now = Date.now();
      if (now - lastALDAttemptRef.current < 1000) {
        logInfo("ALD", "Skipping - too soon since last attempt");
        return getLanguageCode();
      }
      lastALDAttemptRef.current = now;
      const startTime = Date.now();

      try {
        aldInProgressRef.current = true;

        updateFlags({
          detectingLanguage: true,
          processing: true,
          aldRetryCount: aldRetryCountRef.current,
        });

        if (!audioChunks || audioChunks.length === 0) {
          throw new Error("No audio chunks provided for ALD");
        }

        const qualityAssessment = assessAudioQuality(audioChunks);
        if (
          qualityAssessment.quality === "poor" &&
          aldRetryCountRef.current === 0 &&
          qualityAssessment.duration < 0.8
        ) {
          if (
            getIsSocketRecording &&
            continuousSpeechDurationRef.current < 2000
          ) {
            aldInProgressRef.current = false;
            throw new Error(
              `Audio quality insufficient: ${qualityAssessment.reason}`
            );
          }
        }

        const base64Audio = await getCombinedAudioBase64(audioChunks);
        if (!base64Audio || base64Audio.length === 0) {
          throw new Error("Audio conversion resulted in empty base64");
        }

        const audioLengthSec =
          audioChunks.reduce((acc, chunk) => acc + chunk.length, 0) /
          SAMPLE_RATE;
        updateFlags({ audioLength: audioLengthSec.toFixed(1) });

        // ✅ Use unified language detection (NO HTTP HERE)
        //   const base64AudioData = await getCombinedAudioBase64(audioChunks);
        let detectedLang = await detectLanguageFromHookRef.current({
          journeyType: "VOICE",
          audioBase64: base64Audio,
          samplingRate: SAMPLE_RATE,
        });

        // console.log("Detected language from ALD:", detectedLang);

        if (!detectedLang || detectedLang === "unknown") {
          const confirmed = await showErrorAsync(
            t("streamer.languageDetection.failedMessage"),
            {
              title: t("streamer.languageDetection.failedTitle"),
              confirmText: t("streamer.languageDetection.tryAgain"),
            }
          );

          if (confirmed) {
            previousTranscriptRef.current = "";
            setEditableTranscript("");
            clearTranscriptionState();
            handleNewChat();
            await resetToInitialState();
          }

          return null;
        }

        setLanguage(detectedLang);

        const processingTime = Date.now() - startTime;

        dispatch(setSelectedSocketLanguageData({ code: detectedLang }));
        const detectedLabel = getLanguageData(detectedLang)?.label;
        setSelectedLanguageData({
          code: detectedLang,
          label: detectedLabel,
        });
        hasDetectedLanguageRef.current = true;
        aldCompletedRef.current = true;
        aldRetryCountRef.current = 0;

        updateFlags({
          detectingLanguage: false,
          languageDetected: true,
          detectedLanguage: detectedLang,
          aldProcessingTime: processingTime,
          processing: false,
          aldRetryCount: 0,
        });

        const socketPromise = initiateSocket(detectedLang);
        socketPromise.catch((error) => {
          // console.log("SOCKET_INIT_ERROR_FROM_ALD", error.message);
        });
        // if (
        //   !isFinalRecoveryRef.current &&
        //   getIsSocketRecording &&
        //   !fallbackTriggeredRef.current
        // ) {
        //   initiateSocket(detectedLang);
        // }

        return detectedLang;
      } catch (error) {
        const processingTime = Date.now() - startTime;
        aldRetryCountRef.current++;

        // console.log(
        //   "ALD_ERROR",
        //   `Attempt ${aldRetryCountRef.current} failed: ${error.message}`
        // );

        if (
          aldRetryCountRef.current <= ALD_RETRY_ATTEMPTS &&
          getIsSocketRecording &&
          !error.message.includes("Audio quality insufficient")
        ) {
          updateFlags({
            detectingLanguage: false,
            processing: false,
            aldRetryCount: aldRetryCountRef.current,
            error: `ALD retry ${aldRetryCountRef.current}/${ALD_RETRY_ATTEMPTS}: ${error.message}`,
          });

          aldInProgressRef.current = false;

          setTimeout(async () => {
            if (
              getIsSocketRecording &&
              !hasDetectedLanguageRef.current &&
              aldAudioBufferRef.current.length > 0
            ) {
              // console.log(
              //   "ALD_RETRY",
              //   `Retrying language detection (${aldRetryCountRef.current}/${ALD_RETRY_ATTEMPTS})`
              // );
              await detectLanguage(
                [...aldAudioBufferRef.current],
                `${reason}_RETRY_${aldRetryCountRef.current}`
              );
            }
          }, 500);

          return null;
        }

        updateFlags({
          detectingLanguage: false,
          error: `Language detection failed after ${aldRetryCountRef.current} attempts: ${error.message}`,
          processing: false,
          aldProcessingTime: processingTime,
          aldRetryCount: aldRetryCountRef.current,
        });

        logInfo("ALD_FINAL_ERROR", `All attempts failed: ${error.message}`);

        dispatch(setSelectedSocketLanguageData({ code: "en" }));
        hasDetectedLanguageRef.current = true;
        aldCompletedRef.current = true;
        aldRetryCountRef.current = 0;

        initiateSocket("en").catch((socketError) => {
          logInfo("FALLBACK_SOCKET_ERROR", socketError.message);
        });
        // if (!isFinalRecoveryRef.current && getIsSocketRecording) {
        //   initiateSocket("en").catch((socketError) => {
        //     logInfo("FALLBACK_SOCKET_ERROR", socketError.message);
        //   });
        // } else {
        //   logInfo(
        //     "ALD",
        //     "Skipping fallback socket init — final recovery or recording stopped"
        //   );
        // }

        return null;
      } finally {
        aldInProgressRef.current = false;
      }
    };

    const initiateSocket = async (languageCode) => {
      if (isSocketInitializingRef.current && socketInitPromiseRef.current) {
        logInfo(
          "SOCKET_REUSE",
          `Reusing existing socket initialization for language: ${languageCode}`
        );
        return socketInitPromiseRef.current;
      }

      isSocketInitializingRef.current = true;

      socketInitPromiseRef.current = new Promise((resolve, reject) => {
        try {
          updateFlags({ socketConnecting: true, error: null });

          if (socketRef.current?.connected) {
            socketRef.current.disconnect();
          }

          const socket = io(serverUrl, {
            path: socketType === "asr" ? "/socket_vad_asr.io" : "/socket.io",
            transports: ["websocket", "polling"],
            reconnectionDelayMax: BROWSER_SETTINGS.socket.reconnectionDelayMax,
            auth: { authorization: apiKey },
            reconnectionAttempts: BROWSER_SETTINGS.socket.reconnectionAttempts,
            timeout: BROWSER_SETTINGS.socket.timeout,
            forceNew: true,
          });

          socketRef.current = socket;

          socket.on("connect", () => {
            logInfo("SOCKET", "Connected successfully");
            updateFlags({ socketConnecting: false, error: null });
            configurePipeline(languageCode);
          });

          socket.on("ready", () => {
            logInfo("SOCKET", "Ready for audio streaming");
            isSocketReadyRef.current = true;
            updateFlags({ socketReady: true });

            const audioToProcess = [
              ...audioChunksRef.current,
              ...postAldAudioBufferRef.current,
            ];
            if (audioToProcess.length > 0) {
              postAldAudioBufferRef.current = [];
              audioChunksRef.current = [];
              audioChunksRef.current = [...audioToProcess];
            }

            setupAudioSending();
            resolve(socket);
          });

          socket.on("response", (data, metadata) => {
            if (
              isFinalRecoveryRef.current ||
              isProcessingFinalAudioRef.current
            ) {
              return; // 🚫 socket must not mutate transcript now
            }
            const transcript =
              data?.pipelineResponse?.[0]?.output?.[0]?.source?.trim();
            if (!transcript) return;

            hasReceivedTranscriptRef.current = true;

            const isInterim = metadata?.isIntermediateResult === true;

            if (isInterim) {
              dispatch(setCurrentInterimTranscript(transcript));

              // 🔥 INSTANT UI UPDATE
              setEditableTranscript((prev) => {
                const base = accumulatedTranscriptRef.current || "";
                return [base, transcript].filter(Boolean).join(" ");
              });

              dispatch(setTranscriptType("interim"));
              dispatch(updateFlowStates());
            } else {
              if (transcript === lastFinalTranscriptRef.current) {
                // console.log("🚫 Skipping duplicate FINAL transcript");
                return;
              }

              lastFinalTranscriptRef.current = transcript;

              const currentAccumulated = accumulatedTranscriptRef.current || "";
              const newAccumulated = currentAccumulated
                ? `${currentAccumulated} ${transcript}`
                : transcript;

              // Update both refs
              accumulatedTranscriptRef.current = newAccumulated;

              dispatch(setAccumulatedFinalTranscript(newAccumulated));

              // 🔥 INSTANT UI UPDATE
              setEditableTranscript(newAccumulated);

              dispatch(setCurrentInterimTranscript(""));
              dispatch(setTranscriptType("final"));
              dispatch(updateFlowStates());
            }
          });

          socket.on("disconnect", () => {
            updateFlags({ socketReady: false, streaming: false });
            isSocketReadyRef.current = false;
          });

          socket.on("connect_error", (err) => {
            setIsSocketError(true);
            updateFlags({
              socketConnecting: false,
              error: `Socket connection failed: ${err.message}`,
            });
            logInfo("SOCKET_ERROR", err.message, err);
            reject(err);
          });

          socket.on("error", (err) => {
            logInfo("SOCKET_RUNTIME_ERROR", err.message, err);
            updateFlags({ error: `Socket error: ${err.message}` });
            reject(err);
          });

          // setTimeout(() => {
          //   if (!isSocketReadyRef.current) {
          //     const error = new Error("Socket connection timeout after 10s");
          //     logInfo("SOCKET_TIMEOUT", error.message);
          //     reject(error);
          //   }
          // }, 10000);
          setTimeout(() => {
            if (
              !isSocketReadyRef.current &&
              !hasReceivedTranscriptRef.current
            ) {
              logInfo("SOCKET_TIMEOUT", "No ASR response, allowing fallback");
              resolve(socketRef.current);
            }
          }, 10000);
        } catch (error) {
          updateFlags({
            socketConnecting: false,
            error: `Socket setup failed: ${error.message}`,
          });
          logInfo("SOCKET_SETUP_ERROR", error.message, error);
          reject(error);
        }
      });

      try {
        const result = await socketInitPromiseRef.current;
        logInfo("SOCKET_SUCCESS", "Socket initiated successfully");
        return result;
      } finally {
        isSocketInitializingRef.current = false;
        socketInitPromiseRef.current = null;
      }
    };

    const configurePipeline = (lang = "en") => {
      try {
        const payload = [
          {
            taskType: "asr",
            config: {
              language: { sourceLanguage: lang },
              samplingRate: SAMPLE_RATE,
              postProcessors: ["punctuation", "itn"],
              audioFormat: "pcm",
              encoding: "linear16",
              modelType: "latest",
            },
          },
          {
            responseFrequencyInSecs: 0.3,
            responseTaskSequenceDepth: 1,
          },
        ];

        socketRef.current?.emit("start", payload);
        updateFlags({ streaming: true });
        logInfo("PIPELINE", `Configured for language: ${lang}`, payload);
      } catch (error) {
        logInfo("PIPELINE_ERROR", error.message);
        updateFlags({
          error: `Pipeline configuration failed: ${error.message}`,
        });
      }
    };

    const runFinalALDIfNeeded = async () => {
      if (aldCompletedRef.current) {
        return getLanguageCode();
      }

      if (!aldAudioBufferRef.current.length) {
        aldCompletedRef.current = true;
        return null;
      }

      const totalSamples = aldAudioBufferRef.current.reduce(
        (acc, c) => acc + c.length,
        0
      );
      const duration = totalSamples / SAMPLE_RATE;

      if (duration < 0.8) {
        dispatch(setSelectedSocketLanguageData({ code: "en" }));
        hasDetectedLanguageRef.current = true;
        aldCompletedRef.current = true;
        return null;
      }

      try {
        const lang = await detectLanguage(
          [...aldAudioBufferRef.current],
          "STOP_FINAL_ALD"
        );
        aldCompletedRef.current = true;
        return lang;
      } catch {
        dispatch(setSelectedSocketLanguageData({ code: "en" }));
        hasDetectedLanguageRef.current = true;
        aldCompletedRef.current = true;
        return null;
      }
    };

    const fallbackSmallUtteranceASR = async (audioChunks, lang) => {
      try {
        logInfo(
          "ASR_FALLBACK_CHECK",
          `Audio chunks: ${audioChunks?.length || 0}`
        );

        if (aldFailedHardRef.current) {
          logInfo("ASR_FALLBACK_ABORT", "ALD hard failed");
          return;
        }
        // const audioChunks = completeAudioBufferRef.current;

        if (!audioChunks || audioChunks.length === 0) return;

        const duration =
          audioChunks.reduce((acc, c) => acc + c.length, 0) / SAMPLE_RATE;

        const base64Audio = await getCombinedAudioBase64(audioChunks);
        if (!base64Audio) return;

        logInfo(
          "ASR_FALLBACK",
          `Triggering REST ASR fallback (${duration.toFixed(2)}s)`
        );

        await sharedApiService.sendRequest({
          method: "POST",
          url: API_ENDPOINTS.speech2text,
          headers: {
            "Content-Type": "application/json",
          },
          body: {
            audio: base64Audio,
            source_language: lang || getLanguageCode(),
          },
          abortable: true,
          isEncrypted: false,

          onStart: () => {
            logInfo("ASR_FALLBACK_START", "Fallback ASR request started");
            isProcessingFinalAudioRef.current = true;
            // ✅ SHOW UI + disable mic
            setIsFallbackInProgress(true);
          },

          onSuccess: (res) => {
            logInfo("ASR_FALLBACK_SUCCESS", "Fallback ASR succeeded", res);

            // ✅ Expected response shape:
            // { output: "सुबोध", eng_text: "Subodh" }

            const transcript =
              res?.output?.trim() || res?.eng_text?.trim() || "";

            if (!transcript) {
              logInfo("ASR_FALLBACK_EMPTY", "No transcript from fallback ASR");
              return;
            }

            // ✅ Inject transcript as FINAL (same as socket final)
            dispatch(setAccumulatedFinalTranscript(transcript));
            dispatch(setCurrentInterimTranscript(""));
            dispatch(setTranscriptType("final"));
            dispatch(updateFlowStates());

            hasReceivedTranscriptRef.current = true;

            setEditableTranscript(transcript);
          },

          onError: (err) => {
            logInfo("ASR_FALLBACK_ERROR", "Fallback ASR failed", err);
            // ✅ Ensure UI does not get stuck
            setIsFallbackInProgress(false);
          },

          onCancel: (cancelInfo) => {
            logInfo(
              "ASR_FALLBACK_CANCEL",
              "Fallback ASR request cancelled",
              cancelInfo
            );
          },

          onComplete: () => {
            logInfo("ASR_FALLBACK_COMPLETE", "Fallback ASR request completed");
            // ✅ HIDE UI + enable mic
            setIsFallbackInProgress(false);
          },
        });
      } catch (err) {
        logInfo("ASR_FALLBACK_FATAL", err.message);
        // ✅ Ensure UI does not get stuck
        setIsFallbackInProgress(false);
      }
    };

    const setupAudioSending = () => {
      if (sendIntervalRef.current) return;

      sendIntervalRef.current = setInterval(async () => {
        try {
          if (
            !audioChunksRef.current.length ||
            !isSocketReadyRef.current ||
            isProcessingFinalAudioRef.current ||
            !socketRef.current?.connected
          ) {
            return;
          }

          const chunksToSend = [...audioChunksRef.current];
          audioChunksRef.current = [];

          if (chunksToSend.length === 0) return;

          const totalLength = chunksToSend.reduce(
            (acc, chunk) => acc + chunk?.length || 0,
            0
          );
          if (totalLength === 0) return;

          const combined = new Float32Array(totalLength);
          let offset = 0;

          for (const chunk of chunksToSend) {
            if (chunk && chunk.length) {
              combined.set(chunk, offset);
              offset += chunk.length;
            }
          }

          const pcmData = convertAudioData(combined);
          if (!pcmData.length) return;

          socketRef.current.emit(
            "data",
            { audio: [{ audioContent: pcmData }] },
            { responseTaskSequenceDepth: 1 },
            false,
            false
          );
        } catch (error) {
          logInfo("AUDIO_SEND_ERROR", error.message);
          updateFlags({ error: `Audio sending failed: ${error.message}` });
        }
      }, 50);
    };

    const getSafariCompatibleVADOptions = () => {
      const baseOptions = getDefaultRealTimeVADOptions("v5");

      if (BROWSER_TYPE === "safari") {
        return {
          ...baseOptions,
          workletURL: null,
          modelURL:
            "https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.7/dist/silero_vad.onnx",
          positiveSpeechThreshold: 0.8,
          negativeSpeechThreshold: 0.5,
        };
      }

      return baseOptions;
    };

    const vad = useMicVAD({
      ...getSafariCompatibleVADOptions(),
      startOnLoad: false,
      workletURL: undefined,
      modelURL: undefined,
      userSpeakingThreshold: BROWSER_SETTINGS.vad.userSpeakingThreshold,
      redemptionFrames: 15,
      frameSamples: 512,
      onSpeechStart: () => {
        isUserSpeaking.current = true;
        speechStartTimeRef.current = Date.now();
        continuousSpeechDurationRef.current = 0;

        clearTimeout(silenceTimeoutRef.current);
        if (!isSilenceProcessingRef.current) {
          clearTimeout(gracePeriodTimeoutRef.current);
        }

        updateFlags({ listening: true, bufferStatus: "collecting" });

        dispatch(setIsSocketRecording(true));
      },
      onSpeechEnd: () => {
        isUserSpeaking.current = false;
        logInfo("VAD", "Speech ended — waiting for manual stop");
      },
      onVADMisfire: () => {
        logInfo("VAD", "Misfire ignored — manual control enabled");
      },
      onFrameProcessed: (_, frame) => {
        if (frame && frame.length > 0) {
          // intentionally no-op
        }
      },
      onError: (error) => {
        logInfo("VAD_ERROR", `VAD failed: ${error.message}`);
        // Don't auto-stop on VAD errors
        updateFlags({
          error: `Voice detection error: ${error.message}`,
          listening: false,
        });
      },
    });

    const resampleAudio = (audioData, originalSampleRate, targetSampleRate) => {
      if (originalSampleRate === targetSampleRate) {
        return audioData;
      }

      const ratio = originalSampleRate / targetSampleRate;
      const newLength = Math.round(audioData.length / ratio);
      const result = new Float32Array(newLength);

      for (let i = 0; i < newLength; i++) {
        const index = i * ratio;
        const indexFloor = Math.floor(index);
        const indexCeil = Math.min(Math.ceil(index), audioData.length - 1);
        const fraction = index - indexFloor;

        if (indexCeil === indexFloor) {
          result[i] = audioData[indexFloor];
        } else {
          result[i] =
            audioData[indexFloor] * (1 - fraction) +
            audioData[indexCeil] * fraction;
        }
      }

      return result;
    };

    const clearTranscriptionState = () => {
      logInfo("CLEAR_TRANSCRIPTION", "Clearing transcription-related state");

      // 🔹 Local UI
      setEditableTranscript("");
      setIsFallbackInProgress(false);
      setIsSocketError(false);

      // 🔹 Redux transcription
      dispatch(setDisplayTranscript(""));
      dispatch(setAccumulatedFinalTranscript(""));
      dispatch(setCurrentInterimTranscript(""));
      dispatch(setTranscriptType(""));

      // 🔹 Internal transcript refs
      accumulatedTranscriptRef.current = "";
      lastFinalTranscriptRef.current = "";
      hasReceivedTranscriptRef.current = false;

      // 🔹 Recovery flags
      fallbackTriggeredRef.current = false;
      isFinalRecoveryRef.current = false;
      isProcessingFinalAudioRef.current = false;

      // 🔹 Audio buffers (transcript-related only)
      audioChunksRef.current = [];
      postAldAudioBufferRef.current = [];
      completeAudioBufferRef.current = [];

      totalAudioDurationRef.current = 0;
      continuousSpeechDurationRef.current = 0;
    };

    const resetToInitialState = async () => {
      logInfo("RESET", "Resetting to initial state after ALD failure");
      dispatch(setIsSuggestionsVisible(true));

      aldFailedHardRef.current = true;

      // Stop everything safely
      await stopStreaming();

      // Explicitly clear ALD-related refs
      hasDetectedLanguageRef.current = false;
      aldCompletedRef.current = false;
      aldInProgressRef.current = false;
      aldRetryCountRef.current = 0;
      lastALDAttemptRef.current = 0;

      // Clear language from redux
      dispatch(setSelectedSocketLanguageData({}));

      // Reset UI flags
      updateFlags({
        listening: false,
        streaming: false,
        detectingLanguage: false,
        languageDetected: false,
        detectedLanguage: "",
        bufferStatus: "idle",
        processing: false,
        socketConnecting: false,
        socketReady: false,
        error: null,
      });
    };

    const startStreaming = async () => {
      // console.log("Start Streaming -11");

      if (getIsSocketRecording) {
        logInfo("START_STREAMING", "Already recording, ignoring start request");
        return;
      }

      // 🔥 STEP 1: CLEAR ONLY TRANSCRIPTION STATE
      clearTranscriptionState();

      aldFailedHardRef.current = false;

      dispatch(setIsMicButtonClicked(true));
      dispatch(setIsSocketRecording(true));
      updateFlags({ listening: true });
      try {
        if (BROWSER_TYPE === "safari" && !audioContextRef.current) {
          audioContextRef.current = new AudioContext({
            latencyHint: BROWSER_SETTINGS.audioContext.latencyHint,
          });

          // Force resume immediately
          if (audioContextRef.current.state === "suspended") {
            await audioContextRef.current.resume();
            logInfo("SAFARI_AUDIO", "AudioContext resumed after user gesture");
          }
        }

        setIsLoading(true);
        fallbackTriggeredRef.current = false;
        hasReceivedTranscriptRef.current = false;
        isFinalRecoveryRef.current = false;
        accumulatedTranscriptRef.current = "";
        lastFinalTranscriptRef.current = "";

        dispatch(setDisplayTranscript(""));
        dispatch(setAccumulatedFinalTranscript(""));
        dispatch(setCurrentInterimTranscript(""));
        dispatch(setTranscriptType(""));
        setEditableTranscript("");
        updateFlags({
          initializing: true,
          error: null,
          languageDetected: isLanguageExist(),
          audioLength: 0,
          aldProcessingTime: 0,
          audioQuality: "unknown",
          bufferStatus: "initializing",
          aldRetryCount: 0,
        });

        logInfo("START_STREAMING", "Initializing audio streaming");

        // Reset all buffers and state
        audioChunksRef.current = [];
        aldAudioBufferRef.current = [];
        audioQualityBufferRef.current = [];
        postAldAudioBufferRef.current = [];
        completeAudioBufferRef.current = [];
        isSocketReadyRef.current = false;
        totalAudioDurationRef.current = 0;
        isProcessingFinalAudioRef.current = false;
        continuousSpeechDurationRef.current = 0;

        if (!isLanguageExist()) {
          aldInProgressRef.current = false;
          hasDetectedLanguageRef.current = false;
          aldRetryCountRef.current = 0;
          lastALDAttemptRef.current = 0;
        }

        const languageToUse = getLanguageCode();
        logInfo(
          "START_STREAMING",
          `Using language: ${languageToUse} (predefined: ${isLanguageExist()})`
        );

        if (isLanguageExist()) {
          updateFlags({
            detectingLanguage: false,
            languageDetected: true,
            detectedLanguage: languageToUse,
            bufferStatus: "ready",
          });
          // await initiateSocket(languageToUse);
          initiateSocket(languageToUse).catch((err) => {
            logInfo("SOCKET_INIT_ERROR", err.message);
          });
        } else {
          updateFlags({ bufferStatus: "awaiting_language" });
        }

        let stream;
        try {
          const audioConstraints = {
            channelCount: BROWSER_SETTINGS.audio.channelCount,
            echoCancellation: BROWSER_SETTINGS.audio.echoCancellation,
            noiseSuppression: BROWSER_SETTINGS.audio.noiseSuppression,
            autoGainControl: BROWSER_SETTINGS.audio.autoGainControl,
            latency: BROWSER_SETTINGS.audio.latency,
            volume: BROWSER_SETTINGS.audio.volume,
          };

          if (BROWSER_TYPE === "safari") {
            // Safari doesn't like some constraints - keep it simple
            stream = await navigator.mediaDevices.getUserMedia({
              audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              },
            });
            logInfo(
              "SAFARI_AUDIO",
              "Using simplified audio constraints for Safari"
            );
          } else if (BROWSER_TYPE === "firefox") {
            // Firefox-specific code...
            Object.assign(audioConstraints, {
              mozEchoCancellation: BROWSER_SETTINGS.audio.mozEchoCancellation,
              mozNoiseSuppression: BROWSER_SETTINGS.audio.mozNoiseSuppression,
              mozAutoGainControl: BROWSER_SETTINGS.audio.mozAutoGainControl,
            });
            stream = await navigator.mediaDevices.getUserMedia({
              audio: audioConstraints,
            });
          } else {
            audioConstraints.sampleRate = { ideal: SAMPLE_RATE };
            stream = await navigator.mediaDevices.getUserMedia({
              audio: audioConstraints,
            });
          }
        } catch (err) {
          logInfo(
            "AUDIO_SETUP_ERROR",
            `getUserMedia failed: ${err.name} - ${err.message}`,
            { err }
          );

          // Better error handling
          if (
            err.name === "NotAllowedError" ||
            err.name === "PermissionDeniedError"
          ) {
            updateFlags({
              error:
                "Microphone permission denied. Please allow access in browser settings.",
              initializing: false,
              browserCompatible: false,
            });
            setIsLoading(false);
            return;
          }

          // Fallback...
          logInfo(
            "AUDIO_SETUP_WARNING",
            `Failed to get media with constraints, trying fallback: ${err.message}`
          );
          try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          } catch (fallbackErr) {
            updateFlags({
              error: `Microphone access failed: ${fallbackErr.message}`,
              initializing: false,
            });
            setIsLoading(false);
            return;
          }
        }

        mediaStreamRef.current = stream;

        const audioTrack = stream.getAudioTracks()[0];
        const streamSettings = audioTrack.getSettings();
        const actualSampleRate = streamSettings.sampleRate || 48000;

        logInfo(
          "AUDIO_SETUP",
          `Stream sample rate: ${actualSampleRate}Hz, Browser: ${BROWSER_TYPE}`
        );

        let audioContextSampleRate = actualSampleRate;
        if (BROWSER_TYPE === "safari") {
          // ✅ Safari: ALWAYS use native sample rate
          audioContextRef.current = new AudioContext({
            latencyHint: BROWSER_SETTINGS.audioContext.latencyHint,
            // DO NOT set sampleRate - let Safari use its default
          });
          audioContextSampleRate = audioContextRef.current.sampleRate; // Usually 44100 or 48000
          logInfo(
            "SAFARI_AUDIO",
            `Using native sample rate: ${audioContextSampleRate}Hz`
          );
        } else if (BROWSER_TYPE !== "firefox") {
          try {
            audioContextRef.current = new AudioContext({
              sampleRate: SAMPLE_RATE,
              latencyHint: BROWSER_SETTINGS.audioContext.latencyHint,
            });
          } catch (err) {
            logInfo(
              "AUDIO_CONTEXT_WARNING",
              `Failed to create AudioContext with sample rate ${SAMPLE_RATE}: ${err.message}`
            );
            audioContextRef.current = new AudioContext({
              sampleRate: actualSampleRate,
              latencyHint: BROWSER_SETTINGS.audioContext.latencyHint,
            });
          }
        } else {
          audioContextRef.current = new AudioContext({
            sampleRate: actualSampleRate,
            latencyHint: BROWSER_SETTINGS.audioContext.latencyHint,
          });
        }

        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
        }

        const sourceNode =
          audioContextRef.current.createMediaStreamSource(stream);
        const processorNode = audioContextRef.current.createScriptProcessor(
          BROWSER_SETTINGS.scriptProcessor.bufferSize,
          BROWSER_SETTINGS.scriptProcessor.inputChannels,
          BROWSER_SETTINGS.scriptProcessor.outputChannels
        );

        processorNode.onaudioprocess = async (e) => {
          try {
            if (!audioContextRef.current) return;
            if (audioContextRef.current.state === "closed") return;
            if (isProcessingFinalAudioRef.current || !e.inputBuffer) return;

            const inputData = e.inputBuffer.getChannelData(0);
            if (!inputData || inputData.length === 0) return;

            const chunkCopy = new Float32Array(inputData.length);
            for (let i = 0; i < inputData.length; i++) {
              chunkCopy[i] = inputData[i];
            }

            const originalSampleRate = audioContextRef.current.sampleRate;
            const processedChunk = resampleAudio(
              chunkCopy,
              originalSampleRate,
              SAMPLE_RATE
            );

            const chunkRMS = Math.sqrt(
              processedChunk.reduce((sum, sample) => sum + sample * sample, 0) /
                processedChunk.length
            );

            if (chunkRMS > BROWSER_SETTINGS.quality.minRMS) {
              completeAudioBufferRef.current.push(processedChunk);
              if (hasDetectedLanguageRef.current && isSocketReadyRef.current) {
                audioChunksRef.current.push(processedChunk);
              } else if (
                hasDetectedLanguageRef.current &&
                !isSocketReadyRef.current
              ) {
                postAldAudioBufferRef.current.push(processedChunk);
              } else {
                aldAudioBufferRef.current.push(processedChunk);
                if (aldInProgressRef.current) {
                  postAldAudioBufferRef.current.push(processedChunk);
                } else {
                  audioChunksRef.current.push(processedChunk);
                }
              }

              audioQualityBufferRef.current.push(chunkRMS);

              if (
                aldAudioBufferRef.current.length * processedChunk.length >
                MAX_BUFFER_SIZE
              ) {
                aldAudioBufferRef.current = aldAudioBufferRef.current.slice(
                  -Math.floor(MAX_BUFFER_SIZE / processedChunk.length)
                );
                logInfo(
                  "BUFFER_MANAGEMENT",
                  "ALD buffer trimmed to prevent overflow"
                );
              }

              totalAudioDurationRef.current +=
                processedChunk.length / SAMPLE_RATE;
              updateFlags({
                audioLength: totalAudioDurationRef.current.toFixed(1),
                bufferStatus:
                  isLanguageExist() || hasDetectedLanguageRef.current
                    ? "streaming"
                    : "collecting",
              });

              if (
                !isLanguageExist() &&
                !hasDetectedLanguageRef.current &&
                !aldInProgressRef.current
              ) {
                const totalSamples = aldAudioBufferRef.current.reduce(
                  (acc, chunk) => acc + chunk.length,
                  0
                );
                const currentDuration = totalSamples / SAMPLE_RATE;

                const avgQuality =
                  audioQualityBufferRef.current.reduce(
                    (sum, rms) => sum + rms,
                    0
                  ) / audioQualityBufferRef.current.length;
                const hasGoodQuality =
                  avgQuality > BROWSER_SETTINGS.quality.aldQualityThreshold;
                const hasMinDuration = currentDuration >= 6;
                const hasGoodEnoughQuality =
                  avgQuality >
                  BROWSER_SETTINGS.quality.aldQualityThreshold * 0.7;
                const hasSufficientSpeech =
                  isUserSpeaking.current &&
                  continuousSpeechDurationRef.current > 1500;

                if (
                  (hasGoodQuality && hasMinDuration) ||
                  (hasGoodEnoughQuality && hasSufficientSpeech) ||
                  currentDuration >= 6
                ) {
                  let reason;

                  if (hasMinDuration) {
                    reason = `DURATION_${currentDuration.toFixed(1)}S`;
                  } else if (hasSufficientSpeech) {
                    reason = `SPEECH_${(
                      continuousSpeechDurationRef.current / 1000
                    ).toFixed(1)}S`;
                  } else {
                    reason = `FORCE_${currentDuration.toFixed(1)}S`;
                  }

                  let audioForALD;
                  if (currentDuration > 6) {
                    const samplesFor6Seconds = 6 * SAMPLE_RATE;
                    let totalSamplesCollected = 0;
                    const chunks6Seconds = [];

                    for (
                      let i = aldAudioBufferRef.current.length - 1;
                      i >= 0;
                      i--
                    ) {
                      const chunk = aldAudioBufferRef.current[i];
                      if (
                        totalSamplesCollected + chunk.length <=
                        samplesFor6Seconds
                      ) {
                        chunks6Seconds.unshift(chunk);
                        totalSamplesCollected += chunk.length;
                      } else {
                        const remainingSamples =
                          samplesFor6Seconds - totalSamplesCollected;
                        if (remainingSamples > 0) {
                          const partialChunk = chunk.slice(-remainingSamples);
                          chunks6Seconds.unshift(partialChunk);
                        }
                        break;
                      }
                    }
                    audioForALD = chunks6Seconds;
                  } else {
                    audioForALD = [...aldAudioBufferRef.current];
                  }

                  detectLanguage(audioForALD, reason).catch((err) => {
                    logInfo("ALD_TRIGGER_ERROR", err.message);
                  });
                }
              }
            }
          } catch (error) {
            updateFlags({ error: `Audio processing error: ${error.message}` });
            logInfo("AUDIO_PROCESS_ERROR", error.message, error);
          }
        };

        sourceNode.connect(processorNode);
        processorNode.connect(audioContextRef.current.destination);
        sourceNodeRef.current = sourceNode;
        processorNodeRef.current = processorNode;

        if (BROWSER_TYPE === "safari") {
          await new Promise((resolve) => setTimeout(resolve, 100));
          logInfo("SAFARI_AUDIO", "Delayed VAD start for Safari compatibility");
        }

        vad.start();

        updateFlags({
          initializing: false,
          bufferStatus: isLanguageExist() ? "ready" : "collecting",
        });
      } catch (err) {
        updateFlags({
          initializing: false,
          error: `Failed to start streaming: ${err.message}`,
          bufferStatus: "error",
        });
        logInfo("STREAM_START_ERROR", err.message, err);
        await stopStreaming();
      } finally {
        setIsLoading(false);
      }
    };

    const getCompleteTranscript = () => {
      return (
        accumulatedFinalTranscript +
        (accumulatedFinalTranscript && currentInterimTranscript ? " " : "") +
        currentInterimTranscript
      );
    };

    const handleSend = async () => {
      const text = editableTranscript?.trim();
      if (!text) return;

      try {
        // 1️⃣ Stop mic flags if somehow still active
        dispatch(setIsMicButtonClicked(false));
        dispatch(setIsBigMicButtonClicked(false));

        // 2️⃣ Ensure transcript is stored in redux
        dispatch(setAccumulatedFinalTranscript(text));
        dispatch(setCurrentInterimTranscript(""));
        dispatch(setTranscriptType("final"));

        // 3️⃣ Trigger main flow (this should call your API)
        dispatch(updateFlowStates());

        // 4️⃣ Clear local UI after send
        setEditableTranscript("");

        // Optional: Clear redux transcript
        dispatch(setDisplayTranscript(""));

        // 5️⃣ Hit Send Message Button
        if (onAutoSend) {
          onAutoSend({
            text,
            fromSocket: true,
          });
        }
      } catch (err) {
        logInfo("SEND_ERROR", err.message);
      }
    };

    const stopStreaming = async () => {
      // ✅ STEP 0: Guard — avoid double stop
      if (!getIsSocketRecording) return;
      if (isFinalRecoveryRef.current) return;
      isFinalRecoveryRef.current = true;

      // ✅ STEP 1: IMMEDIATE UI RESET (NO await)
      dispatch(setIsMicButtonClicked(false));
      // dispatch(setIsSocketRecording(false));

      logInfo("FINAL_STATE", {
        finalRecovery: isFinalRecoveryRef.current,
        hasTranscript: hasReceivedTranscriptRef.current,
        hasLanguage: hasDetectedLanguageRef.current || isLanguageExist(),
        audioDuration: totalAudioDurationRef.current,
      });

      // Reset UI-affecting refs
      isUserSpeaking.current = false;
      isProcessingFinalAudioRef.current = false;
      isSilenceProcessingRef.current = false;

      try {
        // ✅ STEP 2: Trigger fallback ASR in BACKGROUND (NO await)
        const frozenAudioBuffer = completeAudioBufferRef.current.map(
          (c) => new Float32Array(c)
        );
        const frozenAudioDuration = totalAudioDurationRef.current;

        const hasAudio =
          frozenAudioBuffer.length > 0 && frozenAudioDuration > 0.3;

        // const shouldAttemptRecovery =
        //   hasAudio &&
        //   !hasReceivedTranscriptRef.current &&
        //   !fallbackTriggeredRef.current;
        const shouldAttemptRecovery =
          hasAudio &&
          !hasReceivedTranscriptRef.current &&
          !fallbackTriggeredRef.current &&
          !aldFailedHardRef.current; // 🚫 block fallback

        if (shouldAttemptRecovery) {
          fallbackTriggeredRef.current = true;
          // ✅ SHOW UI IMMEDIATELY (same tick)
          setIsFallbackInProgress(true);
          logInfo(
            "FINAL_RECOVERY",
            "No transcript received — resolving ALD then fallback"
          );

          // 🚨 DO NOT block UI
          (async () => {
            try {
              if (aldFailedHardRef.current) {
                logInfo(
                  "FINAL_RECOVERY_ABORT",
                  "ALD hard failed — skipping fallback"
                );
                // ✅ SHOW UI IMMEDIATELY (same tick)
                setIsFallbackInProgress(false);
                return; // ⛔ HARD STOP
              }

              const lang = await runFinalALDIfNeeded();

              if (!lang || lang === "unknown") {
                logInfo(
                  "FINAL_RECOVERY_ABORT",
                  "Invalid language — skipping fallback"
                );
                setIsFallbackInProgress(false);
                return; // ⛔ HARD STOP
              }

              await fallbackSmallUtteranceASR(frozenAudioBuffer, lang);
            } catch (e) {
              logInfo("FINAL_RECOVERY_ERROR", e.message);
              setIsFallbackInProgress(false);
            }
          })();
        }

        // ✅ STEP 3: Cancel ALD request
        if (aldRequestIdRef.current) {
          logInfo("STOP_STREAMING", "Cancelling ongoing ALD request", {
            requestId: aldRequestIdRef.current,
          });
          sharedApiService.cancelRequest(aldRequestIdRef.current);
          aldRequestIdRef.current = null;
        }

        // ✅ STEP 4: Clear timers / intervals
        clearTimeout(silenceTimeoutRef.current);
        clearTimeout(gracePeriodTimeoutRef.current);
        clearInterval(sendIntervalRef.current);
        sendIntervalRef.current = null;

        accumulatedTranscriptRef.current = "";
        lastFinalTranscriptRef.current = "";

        isSocketInitializingRef.current = false;
        socketInitPromiseRef.current = null;

        // ✅ STEP 5: Stop VAD
        try {
          vad.pause();
        } catch (vadError) {
          logInfo("VAD_STOP_ERROR", vadError.message);
        }

        // ✅ STEP 6: Audio cleanup
        try {
          if (processorNodeRef.current) {
            processorNodeRef.current.onaudioprocess = null;
            processorNodeRef.current.disconnect();
            processorNodeRef.current = null;
          }

          if (sourceNodeRef.current) {
            sourceNodeRef.current.disconnect();
            sourceNodeRef.current = null;
          }

          if (audioContextRef.current) {
            try {
              await audioContextRef.current.close();
            } catch {}
            audioContextRef.current = null;
          }
        } catch (audioError) {
          logInfo("AUDIO_CLEANUP_ERROR", audioError.message);
        }

        // ✅ STEP 7: Socket cleanup
        try {
          if (socketRef.current?.connected) {
            socketRef.current.emit("end");
            socketRef.current.disconnect();
          }
          socketRef.current = null;
          isSocketReadyRef.current = false;

          updateFlags({
            listening: false,
            streaming: false,
            socketReady: false,
            detectingLanguage: false,
            socketConnecting: false,
            processing: false,
            initializing: false,
            bufferStatus: "idle",
            error: null,
          });

          // ✅ NOW hide transcript + stop recording
          dispatch(setIsSocketRecording(false));
        } catch (socketError) {
          logInfo("SOCKET_CLEANUP_ERROR", socketError.message);
        }

        // ✅ STEP 8: Save audio (non-UI critical, safe to await)
        if (completeAudioBufferRef.current.length > 0) {
          try {
            const base64Audio = await getCombinedAudioBase64(
              completeAudioBufferRef.current
            );

            if (base64Audio) {
              dispatch(setRecordedBase64Data(base64Audio));

              const currentText =
                accumulatedFinalTranscript + currentInterimTranscript;

              const audioDuration = totalAudioDurationRef.current;

              // 🧠 NEW: Final completion fallback logic
              const shouldForceCompletion =
                currentText &&
                currentText.split(" ").length > 2 && // avoid tiny utterances
                audioDuration > 1.5; // enough speech recorded

              // if (shouldForceCompletion && startedFromBigMicRef.current) {
              if (shouldForceCompletion) {
                logInfo("FINAL_COMPLETION_CHECK", {
                  text: currentText,
                  duration: audioDuration,
                });

                // Run REST ASR in background to fix last words
                callRestASRAsFallback(base64Audio);
              }

              dispatch(updateFlowStates());
            }
          } catch (saveError) {
            logInfo(
              "AUDIO_SAVE_ERROR",
              `Failed to save recording: ${saveError.message}`
            );
          }
        }
      } catch (error) {
        logInfo("STOP_ERROR", error.message, error);
      } finally {
        // ✅ STEP 9: Buffer cleanup ONLY (no UI state here)
        // startedFromBigMicRef.current = false; // ✅ RESET
        audioChunksRef.current = [];
        aldAudioBufferRef.current = [];
        audioQualityBufferRef.current = [];
        postAldAudioBufferRef.current = [];
        completeAudioBufferRef.current = [];
        totalAudioDurationRef.current = 0;
        continuousSpeechDurationRef.current = 0;

        setIsSocketError(false);
      }
    };

    useImperativeHandle(ref, () => ({
      startStreaming,
      stopStreaming,
      detectLanguage,
      initiateSocket,
      isLanguageExist,
      getLanguageCode,
      assessAudioQuality: () => assessAudioQuality(aldAudioBufferRef.current),
      getBufferStatus: () => ({
        aldBuffer: aldAudioBufferRef.current.length,
        audioBuffer: audioChunksRef.current.length,
        postAldBuffer: postAldAudioBufferRef.current.length,
        totalDuration: totalAudioDurationRef.current,
        hasLanguage: isLanguageExist(),
        languageDetected: hasDetectedLanguageRef.current,
      }),
      forceLanguageDetection: async () => {
        if (aldAudioBufferRef.current.length > 0) {
          return await detectLanguage(
            [...aldAudioBufferRef.current],
            "MANUAL_TRIGGER"
          );
        }
        throw new Error("No audio buffer available for language detection");
      },
      getCompleteTranscript: () => getCompleteTranscript(),
      getAccumulatedFinal: () => accumulatedFinalTranscript,
      getCurrentInterim: () => currentInterimTranscript,
    }));

    useEffect(() => {
      return () => {
        logInfo("COMPONENT_UNMOUNT", "Cleaning up component");
        stopStreaming();
      };
    }, []);

    useEffect(() => {
      const combined =
        accumulatedFinalTranscript +
        (accumulatedFinalTranscript && currentInterimTranscript ? " " : "") +
        currentInterimTranscript;
      dispatch(setDisplayTranscript(combined));
    }, [accumulatedFinalTranscript, currentInterimTranscript]);

    const getButtonState = () => {
      if (isSocketErr || flags.initializing) return "disabled";
      if (flags.error) return "error";
      if (flags.listening) return "listening";
      if (flags.detectingLanguage) return "detecting";
      if (flags.socketConnecting) return "connecting";
      if (flags.processing) return "processing";
      if (getIsSocketRecording) return "recording";
      return "idle";
    };

    const getButtonConfig = (state) => {
      const configs = {
        disabled: {
          className:
            "bg-gradient-to-br from-gray-400 to-gray-500 cursor-not-allowed opacity-60",
          icon: <Loader2 className="animate-spin w-8 h-8 text-white" />,
        },
        error: {
          className:
            "bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-xl shadow-red-500/30",
          icon: <AlertCircle className="w-8 h-8 text-white animate-bounce" />,
        },
        listening: {
          className:
            "bg-gradient-to-br from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-xl shadow-emerald-500/30",
          icon: (
            <div className="relative flex items-center justify-center">
              <div className="relative w-8 h-8">
                <div className="absolute inset-0 rounded-full bg-white/20 animate-ping"></div>
                <div className="absolute inset-2 rounded-full bg-white animate-pulse"></div>
                <div className="absolute inset-3 rounded-full bg-emerald-600"></div>
              </div>
            </div>
          ),
        },
        detecting: {
          className:
            "bg-gradient-to-br from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 shadow-xl shadow-violet-500/30",
          icon: (
            <div className="relative">
              <Globe className="w-8 h-8 text-white animate-pulse" />
              <div className="absolute -inset-2 border border-white/40 rounded-full animate-spin" />
            </div>
          ),
        },
        connecting: {
          className:
            "bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-xl shadow-amber-500/30",
          icon: (
            <div className="flex flex-col items-center space-y-1">
              <div className="flex space-x-1">
                {[0, 200, 400].map((delay, idx) => (
                  <div
                    key={delay}
                    className="w-2 h-2 bg-white rounded-full animate-bounce"
                    style={{ animationDelay: `${delay}ms` }}
                  />
                ))}
              </div>
              <div className="text-xs text-white/80 font-medium">SYNC</div>
            </div>
          ),
        },
        processing: {
          className:
            "bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 shadow-xl shadow-cyan-500/30",
          icon: (
            <div className="relative">
              <Waves className="w-8 h-8 text-white" />
              <div className="absolute inset-0">
                <div className="absolute top-0 left-1/2 w-0.5 h-8 bg-white/60 animate-pulse transform -translate-x-0.5"></div>
                <div className="absolute top-1/2 left-0 w-8 h-0.5 bg-white/60 animate-pulse transform -translate-y-0.5"></div>
              </div>
            </div>
          ),
        },
        recording: {
          className:
            "bg-gradient-to-br from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 shadow-xl shadow-red-600/40 animate-pulse",
          icon: (
            <div className="relative flex items-center justify-center">
              <div className="relative w-8 h-8">
                <div className="absolute inset-0 rounded-full bg-white/20 animate-ping"></div>
                <div className="absolute inset-2 rounded-full bg-white animate-pulse"></div>
                <div className="absolute inset-3 rounded-full bg-red-600"></div>
              </div>
            </div>
          ),
        },
        idle: {
          className:
            "bg-gradient-to-br from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-xl shadow-blue-500/30",
          icon: <Mic className="w-6 h-6 text-white drop-shadow-lg" />,
        },
      };
      return configs[state];
    };

    const buttonState = getButtonState();
    const buttonConfig = getButtonConfig(buttonState || "idle");

    const getCircleFillPercentage = () => {
      if (
        hasDetectedLanguageRef.current ||
        isLanguageExist() ||
        !getIsSocketRecording
      ) {
        return 0; // No fill after language detection or when not recording
      }

      const currentDuration = totalAudioDurationRef.current;
      const fillPercentage = Math.min((currentDuration / 6) * 100, 100);
      return fillPercentage;
    };

    const isMicDisabledUI = useMemo(() => {
      return (
        buttonState === "disabled" ||
        isLanguageDetecting ||
        isNerDetecting ||
        isContentGenerating ||
        isFallbackInProgress ||
        isNotRelatedToCenter // ✅ ADD
      );
    }, [
      buttonState,
      isLanguageDetecting,
      isNerDetecting,
      isContentGenerating,
      isFallbackInProgress,
      isNotRelatedToCenter,
    ]);

    const isMicDisabled =
      isMicDisabledUI || isMicButtonDisabled || isNotRelatedToCenter;

    // console.log("Check Disabled UI Issue", {
    //   buttonState: buttonState === "disabled",
    //   isLanguageDetecting,
    //   isNerDetecting,
    //   isContentGenerating,
    //   isFallbackInProgress,
    //   isMicDisabled,
    //   isMicDisabledUI,
    //   isMicButtonDisabled,
    // });

    const showAutoSendTooltip =
      getIsSocketRecording &&
      isLanguageExist() &&
      (currentInterimTranscript || accumulatedFinalTranscript);

    const isDisabled = isMicDisabled;

    const showTapToSpeak =
      !getIsSocketRecording &&
      !isMicDisabled &&
      !isFallbackInProgress &&
      !isNotRelatedToCenter;

    // Debug: Log the state values
    console.log("SocketMicStreamer - State Check:", {
      isNotRelatedToCenter,
      showTapToSpeak,
      getIsSocketRecording,
      isMicDisabled,
      isFallbackInProgress,
    });
    const hasTranscription = Boolean(editableTranscript?.trim());

    const hasAnyTranscript =
      editableTranscript?.trim() ||
      currentInterimTranscript?.trim() ||
      accumulatedFinalTranscript?.trim();

    const showTranscriptSkeleton = getIsSocketRecording && !hasAnyTranscript;

    const multiButtonMode = getIsSocketRecording
      ? "stop"
      : hasTranscription
      ? "send"
      : "disabled";

    const showSendTooltip =
      multiButtonMode === "send" &&
      !isFallbackInProgress &&
      !getIsSocketRecording;

    const TranscriptSkeleton = () => (
      <div className="space-y-3">
        {[1].map((_, i) => (
          <div
            key={i}
            className="relative overflow-hidden rounded-lg bg-gray-100/50 p-3"
          >
            {/* Shimmer effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent"
              animate={{ x: ["-100%", "200%"] }}
              transition={{
                repeat: Infinity,
                duration: 1.8,
                ease: "linear",
                delay: i * 0.2,
              }}
            />

            {/* Content skeleton */}
            <div className="relative space-y-2.5">
              <div className="space-y-2">
                <div
                  className="h-3 rounded-full bg-gray-200/80"
                  style={{ width: `${90 - i * 8}%` }}
                />
                <div
                  className="h-3 rounded-full bg-gray-200/80"
                  style={{ width: `${75 - i * 7}%` }}
                />
                {i === 0 && (
                  <div
                    className="h-3 rounded-full bg-gray-200/80"
                    style={{ width: "60%" }}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );

    const handleToggleMic = async () => {
      try {
        if (getIsSocketRecording) {
          await stopStreaming();
        } else {
          await startStreaming();
        }
      } catch (err) {
        console.error("Mic toggle error:", err);
      }
    };

    return (
      <>
        {normalButton ? (
          <div className="flex items-center">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleToggleMic}
              disabled={isMicDisabled}
              className={`
              p-1 flex items-center justify-center
              transition-opacity duration-200
              ${
                isMicDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "opacity-80 hover:opacity-100"
              }
            `}
            >
              {isMicDisabled ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : getIsSocketRecording ? (
                <Square className="w-5 h-5 text-red-500" />
              ) : (
                <Mic className="w-5 h-5 text-gray-500" />
              )}
            </motion.button>
          </div>
        ) : isUserInputBar ? (
          <>
            <div
              className={`relative ${isMobile ? "mb-[-140px]" : "mt-[85px]"}`}
            >
              <div className="relative">
                {!isMobile && (
                  <img
                    src={micButtonBg}
                    className="img_min_con"
                    alt="Mic background"
                  />
                )}

                <button
                  onClick={() => {
                    if (isMicDisabled || isNotRelatedToCenter) return;
                    dispatch(setIsTextFromBigMicButton(false));
                    dispatch(setIsSendTriggeredFromSocket(false));
                    getIsSocketRecording ? stopStreaming() : startStreaming();
                  }}
                  title={t("streamer.buttons.mic")}
                  disabled={
                    isMicDisabledUI ||
                    isMicButtonDisabled ||
                    isNotRelatedToCenter
                  }
                  className={`
                              mic_btn_inside
                              relative rounded-full
                              flex items-center justify-center
                              transition-all duration-300
                              ${buttonConfig.className}
                              disabled:from-gray-400 disabled:to-gray-500
                              disabled:cursor-not-allowed
                              w-[50px] h-[50px]
                              overflow-hidden group
                            `}
                  style={{
                    width: "50px",
                    height: "50px",
                    backgroundColor: "#FE6700",
                    background: "#FE6700", // overrides any gradient from buttonConfig.className
                  }}
                >
                  <span className="mic-shine absolute inset-0 rounded-full" />

                  <span className="relative z-10 flex items-center justify-center">
                    {buttonConfig.icon}
                  </span>
                </button>

                <AnimatePresence>
                  {showTapToSpeak && (
                    <motion.div
                      initial={{ opacity: 0, x: -8, scale: 0.95 }}
                      animate={{ opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: -8, scale: 0.95 }}
                      transition={{ duration: 0.25 }}
                      className="
                                absolute
                                left-[65px]
                                top-[-70px]
                                -translate-y-1/2
                                bg-[#FE6700]
                                text-[#fff]
                                dark:text-[#FFFFFF]
                                shadow-lg
                                rounded-md
                                text-xs
                                px-3 py-1.5
                                whitespace-nowrap
                              "
                    >
                      {t("tapToSpeak")}
                      <div
                        className="
                                  absolute
                                  left-[-6px]
                                  top-1/2
                                  -translate-y-1/2
                                  w-0 h-0
                                  border-t-[6px]
                                  border-b-[6px]
                                  border-r-[6px]
                                  border-t-transparent
                                  border-b-transparent
                                  border-r-[#f89b44]
                                "
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* ✅ Tooltip for UserInputBar */}
                {/* <TooltipBubble
                show={showAutoSendTooltip}
                message="If auto-send doesn't trigger, tap the mic."
                className="left-[30px] -top-[120px]"
                slideDistance={100}
                animationDuration={1}
                fadeDelay={0.2}
                delayBeforeShow={3}
              /> */}
                {showAutoSendTooltip &&
                  (isMobile ? (
                    <div
                      className="
                          absolute
                          -top-[270px]
                          left-1/2
                          -translate-x-1/2
                          bg-blue-600/30
                          text-black
                          text-[11px]
                          px-3
                          py-1.5
                          rounded-lg
                          drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]
                          max-w-[220px]
                          text-center
                        "
                    >
                      {t("autoSendMessage")}
                    </div>
                  ) : (
                    <TooltipBubble
                      show={showAutoSendTooltip}
                      message={t("autoSendMessage")}
                      className="left-[30px] -top-[120px]"
                      slideDistance={100}
                      animationDuration={1}
                      fadeDelay={0.2}
                      delayBeforeShow={3}
                    />
                  ))}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center space-y-6">
            <div className="relative flex justify-center items-center mt-5 mb-2">
              <motion.div
                className={`absolute w-[140px] h-[140px] rounded-full border-2 transition-all duration-300 ${
                  isDisabled
                    ? "bg-gray-200 border-gray-300"
                    : "bg-[#E2EFFF] border-[#E0E0E0]"
                }`}
                animate={
                  getIsSocketRecording ? { scale: [1, 1.05, 1] } : { scale: 1 }
                }
                transition={{
                  duration: 2,
                  repeat: getIsSocketRecording ? Infinity : 0,
                }}
              />
              <motion.div
                className={`absolute w-[115px] h-[115px] rounded-full transition-all duration-300 ${
                  isDisabled
                    ? "bg-gray-400 shadow-none"
                    : "bg-[#FE6700] shadow-lg"
                }`}
                transition={{
                  duration: 1.5,
                  repeat: getIsSocketRecording ? Infinity : 0,
                  ease: "easeInOut",
                }}
              />

              <motion.button
                whileHover={{
                  scale: isDisabled || isNotRelatedToCenter ? 1 : 1.05,
                }}
                whileTap={{
                  scale: isDisabled || isNotRelatedToCenter ? 1 : 0.95,
                }}
                onClick={() => {
                  if (isMicDisabled || isNotRelatedToCenter) return;
                  if (getIsSocketRecording) {
                    stopStreaming();
                  } else {
                    // startedFromBigMicRef.current = true;
                    dispatch(setIsBigMicButtonClicked(true));
                    dispatch(updateFlowStates());
                    startStreaming();
                  }
                }}
                disabled={isMicDisabled || isNotRelatedToCenter}
                className={`relative w-[90px] h-[90px] rounded-full flex justify-center items-center transition-all duration-300 ${
                  isDisabled || isNotRelatedToCenter
                    ? "bg-gray-100 cursor-not-allowed opacity-70"
                    : "bg-white cursor-pointer hover:shadow-2xl"
                }`}
                style={{
                  boxShadow: isDisabled
                    ? "none"
                    : getIsSocketRecording
                    ? "0 20px 40px rgba(59, 130, 246, 0.3), 0 0 0 4px rgba(59, 130, 246, 0.1)"
                    : "0 10px 25px rgba(0, 0, 0, 0.15)",
                }}
              >
                {getIsSocketRecording ? (
                  <div className="flex flex-col items-center justify-center">
                    <motion.div
                      className="absolute w-[115px] h-[115px] rounded-full overflow-hidden"
                      style={{
                        background: `conic-gradient(from 0deg at 50% 50%, 
                                                #40FA84 0deg, 
                                                #40FA84 ${
                                                  (getCircleFillPercentage() /
                                                    100) *
                                                  360
                                                }deg, 
                                                transparent ${
                                                  (getCircleFillPercentage() /
                                                    100) *
                                                  360
                                                }deg, 
                                                transparent 360deg)`,
                      }}
                      animate={{
                        opacity:
                          getCircleFillPercentage() > 0 &&
                          !hasDetectedLanguageRef.current
                            ? 0.7
                            : 0,
                      }}
                      transition={{ duration: 0.3 }}
                    />
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity }}
                    >
                      <img src={voiceAssistantGif} alt="Recording" />
                    </motion.div>
                  </div>
                ) : (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img src={micIcon} alt="Voice Assistant" />
                    <div className="flex gap-1 mt-2">
                      {bars.map((bar, i) => (
                        <motion.div
                          key={`${bar.height}-${bar.delay}`}
                          className={`w-1/2 bg-gray-500 rounded ${bar.height}`}
                          animate={{
                            scaleY: [1, 1.8, 1],
                            opacity: [0.5, 1, 0.5],
                          }}
                          transition={{
                            duration: 0.8,
                            repeat: Infinity,
                            repeatType: "loop",
                            delay: bar.delay,
                          }}
                          style={{ transformOrigin: "bottom" }}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}

                {isLoading && (
                  <motion.div
                    className="absolute inset-0 rounded-full bg-white/80 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                  </motion.div>
                )}
              </motion.button>

              <TooltipBubble
                show={showAutoSendTooltip}
                message={t("autoSendMessage")}
                className="left-[95px] -top-4"
                slideDistance={100}
                animationDuration={1}
                fadeDelay={0.2}
                delayBeforeShow={3}
              />

              <AnimatePresence>
                {buttonState !== "idle" && buttonState !== "recording" && (
                  <motion.div
                    className="absolute -top-2 -right-2 w-[0px] h-[0px] rounded-full bg-white shadow-lg flex items-center justify-center"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                  >
                    {buttonState === "error" && (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    {buttonState === "detecting" && (
                      <Globe className="w-4 h-4 text-violet-500 animate-pulse" />
                    )}
                    {buttonState === "connecting" && (
                      <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                    )}
                    {buttonState === "processing" && (
                      <Waves className="w-4 h-4 text-cyan-500" />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <AnimatePresence>
              {(getIsSocketRecording ||
                hasTranscription ||
                isFallbackInProgress) && (
                <motion.div
                  className={`relative z-50 w-[calc(100%-20px)] max-w-4xl rounded-md px-4 py-2 border shadow-lg mx-[10px] ${
                    transcriptType === "final"
                      ? "bg-gradient-to-br from-[#FFF7ED] via-[#F3780B]to-[#F3780B] border-[#FFEDD4]"
                      : "bg-gradient-to-br from-[#FFF7ED] via-[#FFF7ED] to-[#FFF7ED] border-[#FFB864]"
                  }`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isLanguageExist() && (
                    <>
                      <div className="flex items-center justify-center">
                        <span className="text-sm text-gray-600 font-semibold">
                          {t("detectedLanguageText")}:{" "}
                          <span className="">
                            {`${getLanguageData(getLanguageCode()).label}`}
                          </span>
                        </span>
                      </div>

                      {/* Show listening text ONLY while recording */}
                      {getIsSocketRecording && (
                        <motion.p
                          className="text-sm text-gray-800 text-center mt-1 font-semibold"
                          animate={{ opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <span>
                            🎤 {t("listeningKeepSpeak")} (
                            {totalAudioDurationRef.current.toFixed(1)}s)
                          </span>
                        </motion.p>
                      )}

                      {/* Show edit hint ONLY after recording stops and transcript exists */}
                      {/* Show edit hint ONLY after recording stops and transcript exists */}
                      {!getIsSocketRecording && editableTranscript?.trim() && (
                        <motion.p
                          className="text-sm text-gray-800 text-center mt-1 font-semibold"
                          animate={{ opacity: [0.6, 1, 0.6] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                        >
                          <span>✏️ {t("editTranscriptHint")}</span>
                        </motion.p>
                      )}
                    </>
                  )}
                  {getIsSocketRecording && !isLanguageExist() && (
                    <div className="flex items-center justify-center">
                      <span className="text-sm text-gray-600">
                        <span className="font-semibold">
                          {t("keepSpkLanguageDetection")}
                        </span>
                      </span>
                    </div>
                  )}

                  {(getIsSocketRecording ||
                    editableTranscript?.trim() ||
                    isFallbackInProgress) && (
                    <div
                      ref={transcriptRef}
                      className="bg-white/70 backdrop-blur-sm rounded-xl p-4 pr-20 border border-white/20 mt-3 relative"
                      style={{
                        maxHeight: `${window.innerHeight - 470}px`,
                      }}
                    >
                      {showTranscriptSkeleton ? (
                        <TranscriptSkeleton />
                      ) : (
                        <KpmgTransliterate
                          lang={getLanguageCode() || "en"}
                          value={editableTranscript}
                          onChangeText={(val) => {
                            const trimmed = val.trim();

                            // 🚨 Detect full deletion AFTER transcription happened
                            if (
                              !trimmed &&
                              previousTranscriptRef.current &&
                              !getIsSocketRecording
                            ) {
                              (async () => {
                                const confirmed = await showConfirm(
                                  t("streamer.transcript.deleteMessage"),
                                  {
                                    title: t("streamer.transcript.deleteTitle"),
                                    confirmText: t(
                                      "streamer.transcript.confirmDelete"
                                    ),
                                    cancelText: t("streamer.transcript.cancel"),
                                  }
                                );

                                if (confirmed) {
                                  // ✅ User confirmed
                                  previousTranscriptRef.current = "";
                                  setEditableTranscript("");
                                  clearTranscriptionState();
                                  // console.log(
                                  //   "Transcript deleted, ready for new recording"
                                  // );
                                  handleNewChat();
                                } else {
                                  // ❌ User cancelled
                                  setEditableTranscript(
                                    previousTranscriptRef.current
                                  );
                                  // console.log(
                                  //   "Deletion cancelled, transcript restored"
                                  // );
                                }
                              })();

                              return;
                            }

                            setEditableTranscript(val);
                            dispatch(setAccumulatedFinalTranscript(val));
                            dispatch(setCurrentInterimTranscript(""));
                          }}
                          containerClassName="w-full"
                          enabled={
                            !getIsSocketRecording &&
                            !isFallbackInProgress &&
                            getLanguageCode() !== "en"
                          }
                          renderComponent={(props) => (
                            <TextareaAutosize
                              {...props}
                              readOnly={
                                getIsSocketRecording || isFallbackInProgress
                              }
                              minRows={3}
                              maxRows={8}
                              className={`
                            w-full resize-none text-lg px-3 py-2 rounded-md bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#FFD6A8]
                            ${
                              getIsSocketRecording || isFallbackInProgress
                                ? "opacity-60 cursor-not-allowed"
                                : ""
                            }
                          `}
                            />
                          )}
                        />
                      )}
                      
                      {getIsSocketRecording && (
                        <motion.div 
                          className="mt-3 text-center text-[14px] text-blue-600 font-semibold animate-pulse"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                        >
                          {t("autoSendMessage")}
                        </motion.div>
                      )}

                      {/* ✅ Small Inline Processing Chip */}
                      {isFallbackInProgress && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="
                            absolute
                            bottom-3
                            right-16
                            flex items-center gap-2
                            px-3 py-1
                            rounded-full
                            bg-blue-50
                            border border-blue-200
                            shadow-sm
                          "
                        >
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              ease: "linear",
                            }}
                          >
                            <Loader2 className="w-3.5 h-3.5 text-blue-600" />
                          </motion.div>

                          <span className="text-xs font-medium text-blue-700">
                            {t("streamer.transcript.processing")}
                          </span>
                        </motion.div>
                      )}
                    </div>
                  )}
                  {/* 🔥 Multifunction Stop/Send Button */}
                  {(editableTranscript?.trim() || isFallbackInProgress) && (
                    <div className="absolute bottom-4 right-4 z-50">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.96 }}
                        disabled={
                          multiButtonMode === "disabled" || isFallbackInProgress
                        }
                        onClick={() => {
                          if (getIsSocketRecording) {
                            stopStreaming();
                          } else {
                            handleSend();
                          }
                        }}
                        title={getIsSocketRecording ? "Stop" : "Send"}
                        className={`
                      bg-gradient-to-r
                      ${
                        getIsSocketRecording
                          ? "from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
                          : editableTranscript?.trim()
                          ? "from-[#FE6700] to-[#FE6700] hover:from-[#FE6700] hover:to-[#FE6700]"
                          : "from-[#FE6700] to-gray-500 cursor-not-allowed"
                      }
                      text-white rounded-full transition-all duration-300
                      hover:shadow-lg
                      ${
                        getIsSocketRecording
                          ? "hover:shadow-red-500/25"
                          : "hover:shadow-[#FE6700]"
                      }
                      disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
                      flex items-center justify-center
                      w-[50px] h-[50px]
                      relative overflow-hidden group
                    `}
                      >
                        {/* Shine animation */}
                        <motion.div
                          animate={{
                            x:
                              !getIsSocketRecording &&
                              !editableTranscript?.trim()
                                ? 0
                                : [-100, 100],
                          }}
                          transition={{
                            duration: 2,
                            repeat:
                              !getIsSocketRecording &&
                              !editableTranscript?.trim()
                                ? 0
                                : Infinity,
                            ease: "easeInOut",
                          }}
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 opacity-0 group-hover:opacity-100"
                        />

                        {/* Icon */}
                        <div className="flex items-center justify-center relative z-10">
                          <motion.div
                            animate={
                              getIsSocketRecording
                                ? { scale: [1, 1.1, 1] }
                                : editableTranscript?.trim()
                                ? { x: [0, 3, 0], rotate: [0, 15, -5, 0] }
                                : { x: 0, rotate: 0 }
                            }
                            transition={{
                              duration: 2.5,
                              repeat:
                                getIsSocketRecording ||
                                editableTranscript?.trim()
                                  ? Infinity
                                  : 0,
                              ease: "easeInOut",
                            }}
                          >
                            {getIsSocketRecording ? (
                              <div className="relative w-4 h-4">
                                <div className="absolute inset-0 rounded-full bg-white animate-ping opacity-50"></div>
                                <div className="absolute inset-1 rounded-full bg-white"></div>
                              </div>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 12h14M13 6l6 6-6 6"
                                />
                              </svg>
                            )}
                          </motion.div>
                        </div>
                      </motion.button>
                      {/* ✅ SAFE TOOLTIP */}
                      <AnimatePresence>
                        {multiButtonMode === "send" &&
                          !isFallbackInProgress &&
                          !getIsSocketRecording && (
                            <motion.div
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 6 }}
                              transition={{ duration: 0.2 }}
                              className="
                            absolute
                            -top-10
                            right-0
                            bg-[#FF6900]
                            text-white
                            text-xs
                            px-3
                            py-1
                            rounded-md
                            shadow-lg
                            whitespace-nowrap
                            pointer-events-none
                          "
                            >
                              {t("send")}
                            </motion.div>
                          )}
                      </AnimatePresence>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        <div style={{ display: "none" }}>{vad.ref}</div>
      </>
    );
  }
);

SocketMicStreamer.propTypes = {
  isUserInputBar: PropTypes.bool,
};

SocketMicStreamer.displayName = "SocketMicStreamer";
export default SocketMicStreamer;
